import { useContext, useEffect, useState } from "react";
import { FilterContext } from "../filtersProvider";
import {
  AvailabilityFilter,
  BrandWithModelsFilter,
  CheckboxFilter,
  DepartmentsFilter,
  DriveWheelFilter,
  EquipmentFilter,
  FuelFilter,
  SalesTypeFilter,
  ModelFilter,
  RangeFilter,
  ColorFilter,
} from "../types/filters.types";

export const useFilters = () => {
  const filterContext = useContext(FilterContext);

  const {
    brandsWithModels,
    setBrandsWithModels,
    fuelFilter,
    setFuelFilter,
    driveWheelFilter,
    setDriveWheelFilter,
    priceRange,
    setPriceRange,
    modelYearRange,
    setModelYearRange,
    mileageRange,
    setMileageRange,
    departmentsFilter,
    setDepartmentsFilter,
    equipmentFilter,
    setEquipmentFilter,
    salesTypeFilter,
    setSalesTypeFilter,
    colorFilter,
    setColorFilter,
    availabilityFilter,
    setAvailabilityFilter
  } = filterContext;

  const initialRangeVal = {
    startRange: 0,
    endRange: 0,
  };

  // temporary storage for the range filter input values
  const [priceInput, setPriceInput] = useState<RangeFilter>(initialRangeVal);
  const [mileageInput, setMileageInput] = useState<RangeFilter>(initialRangeVal);
  const [modelYearInput, setModelYearInput] = useState<RangeFilter>(initialRangeVal);

  useEffect(() => {
    if (priceRange) setPriceInput(priceRange);
    if (mileageRange) setMileageInput(mileageRange);
    if (modelYearRange) setModelYearInput(modelYearRange);
  }, [priceRange, mileageRange, modelYearRange]);

  const toggleBrandsWithModels = (
    toggleBrandKey: string,
    toggleModelKey?: string,
  ) => {
    // loop through all the brands and find which brand (and model if any) we want to toggle on/off
    const updatedFilter = brandsWithModels?.map((brand) => {
      // user toggles a brand
      if (brand.key === toggleBrandKey && !toggleModelKey) {
        if (brand.isActive) {
          // User toggles brand off, then we also want to toggle off all models for that brand
          brand.models.map((m) => (m.isActive = false));
        }

        brand.isActive = !brand.isActive;
      }

      // user toggles a model
      if (toggleModelKey) {
        brand.models = getUpdatedModels(brand.models, toggleModelKey);
      }
      return brand;
    });
    if (updatedFilter) setBrandsWithModels(updatedFilter);
  };

  // helper to toggle off a model and return the updated list of models
  const getUpdatedModels = (models: ModelFilter[], toggleModelKey: string) =>
    models.map(
      (model) =>
        (model.key === toggleModelKey && {
          ...model,
          isActive: !model.isActive,
        }) || {
          ...model,
        },
    );

  const toggleFuelFilter = (toggleFuelKey: string) => {
    toggleDefaultCheckboxFilter(toggleFuelKey, "Fuel", fuelFilter);
  };


  const toggleDriveWheelFilter = (toggleDriveWheelKey: string) => {
    toggleDefaultCheckboxFilter(
      toggleDriveWheelKey,
      "DriveWheel",
      driveWheelFilter,
    );
  };

  const toggleDepartmentsFilter = (toggleDepartmentsKey: string, toggleGroupKey?: string) => {

    // if toggleGroupKey is provided, we're toggling an individual department within a group
    if (toggleGroupKey) {
      toggleDefaultCheckboxFilter(
        toggleGroupKey,
        "Departments",
        departmentsFilter,
      );
      return;
    }

    // Check if we're toggling a group (toggleDepartmentsKey matches a group name)
    const isGroupToggle = departmentsFilter?.some(
      (dep) => dep.group === toggleDepartmentsKey,
    );

    if (isGroupToggle) {
      // Toggle group expansion state (like brand.isActive in BrandWithModels)
      const groupDepartments = departmentsFilter?.filter(
        (dep) => dep.group === toggleDepartmentsKey,
      );
      const isGroupExpanded = groupDepartments?.[0]?.isGroupExpanded ?? false;

      const updatedFilter = departmentsFilter?.map((dep) => {
        if (dep.group === toggleDepartmentsKey) {
          // Toggle group expansion
          dep.isGroupExpanded = !isGroupExpanded;
          // When collapsing group, also deselect all departments in it
          if (isGroupExpanded) {
            dep.isActive = false;
          }
        }
        return dep;
      });

      if (updatedFilter) setDepartmentsFilter(updatedFilter);
    } else {
      // Regular department toggle (no group)
      toggleDefaultCheckboxFilter(
        toggleDepartmentsKey,
        "Departments",
        departmentsFilter,
      );
    }
  };

  const toggleEquipmentFilter = (toggleEquipmentKey: string) => {
    toggleDefaultCheckboxFilter(
      toggleEquipmentKey,
      "Equipment",
      equipmentFilter,
    );
  };

  const toggleSalesTypesFilter = (toggleSalesTypesKey: string) => {
    toggleDefaultCheckboxFilter(
      toggleSalesTypesKey,
      "SalesType",
      salesTypeFilter,
    );
  };

  const toggleColorFilter = (toggleColorFilterKey: string) => {
    toggleDefaultCheckboxFilter(
      toggleColorFilterKey,
      "Color",
      colorFilter,
    );
  }

  const toggleAvailabilityFilter = (toggleAvailabilityKey: string) => {
    toggleDefaultCheckboxFilter(
      toggleAvailabilityKey,
      "Availability",
      availabilityFilter,
    );
  };

  // Toggle-function for all checkbox-filters except BrandWithModels
  const toggleDefaultCheckboxFilter = (
    toggleFilterKey: string,
    kind: Exclude<CheckboxFilter["kind"], BrandWithModelsFilter["kind"]>,
    filter: CheckboxFilter[] | undefined,
  ) => {
    const updatedFilter = filter?.map((f) => {
      if (f.key === toggleFilterKey) {
        f.isActive = !f.isActive;
      }
      return f;
    });
    if (!updatedFilter) return;
    switch (kind) {
      case "Fuel":
        return setFuelFilter(updatedFilter as FuelFilter[]);
      case "DriveWheel":
        return setDriveWheelFilter(updatedFilter as DriveWheelFilter[]);
      case "Departments":
        return setDepartmentsFilter(updatedFilter as DepartmentsFilter[]);
      case "Equipment":
        return setEquipmentFilter(updatedFilter as EquipmentFilter[]);
      case "SalesType":
        return setSalesTypeFilter(updatedFilter as SalesTypeFilter[]);
      case "Color":
        return setColorFilter(updatedFilter as ColorFilter[]);
      case "Availability":
        return setAvailabilityFilter(updatedFilter as AvailabilityFilter[]);
    }
  };

  const onClickSearchRangeFilter = () => {
    setPriceRange(priceInput);
    setModelYearRange(modelYearInput);
    setMileageRange(mileageInput);
  };

  const resetAllFilters = () => {
    if (brandsWithModels)
      setBrandsWithModels(
        brandsWithModels.map((brand) => {
          brand.isActive = false;
          brand.models.map((model) => {
            model.isActive = false;
          });
          return brand;
        }),
      );

    if (fuelFilter)
      setFuelFilter(
        fuelFilter.map((fuel) => {
          return { ...fuel, isActive: false };
        }),
      );

    if (driveWheelFilter)
      setDriveWheelFilter(
        driveWheelFilter.map((dw) => {
          return { ...dw, isActive: false };
        }),
      );

    if (departmentsFilter)
      setDepartmentsFilter(
        departmentsFilter.map((dep) => {
          return { ...dep, isActive: false };
        }),
      );

    if (equipmentFilter)
      setEquipmentFilter(
        equipmentFilter.map((equipment) => {
          return { ...equipment, isActive: false };
        }),
      );

    if (salesTypeFilter)
      setSalesTypeFilter(
        salesTypeFilter.map((salesType) => {
          return { ...salesType, isActive: false };
        }),
      );

    if (availabilityFilter)
      setAvailabilityFilter(
        availabilityFilter.map((a) => {
          return { ...a, isActive: false };
        }),
      );

    setPriceRange(undefined);
    setPriceInput(initialRangeVal);
    setMileageRange(undefined);
    setMileageInput(initialRangeVal);
    setModelYearRange(undefined);
    setModelYearInput(initialRangeVal);
  };

  return {
    priceRange,
    mileageRange,
    modelYearRange,
    brandsWithModels,
    toggleBrandsWithModels,
    fuelFilter,
    toggleFuelFilter,
    driveWheelFilter,
    toggleDriveWheelFilter,
    priceInput,
    setPriceInput,
    modelYearInput,
    setModelYearInput,
    mileageInput,
    setMileageInput,
    departmentsFilter,
    toggleDepartmentsFilter,
    onClickSearchRangeFilter,
    resetAllFilters,
    equipmentFilter,
    toggleEquipmentFilter,
    salesTypeFilter,
    toggleSalesTypesFilter,
    colorFilter,
    toggleColorFilter,
    availabilityFilter,
    toggleAvailabilityFilter
  };
};
